import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import {
  Brain, CalendarClock, Camera, Check, ChevronLeft, ChevronRight, Download, FilePen, FileText, FolderInput, Globe, GraduationCap, KeyRound, ListChecks, Loader2,
  MessageSquare, Monitor, MousePointerClick, Plug, Search, Smartphone, SquareCheckBig, SquarePen, Terminal, Trash2,
  UserRoundCheck, XCircle,
} from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '../ui/dialog';
import { botsApi } from '../../services/api';

const ICONS = {
  create_task: SquareCheckBig, update_task: SquarePen, get_task: SquareCheckBig, delete_task: Trash2,
  list_tasks: ListChecks, list_projects: ListChecks, list_task_comments: MessageSquare, add_task_comment: MessageSquare,
  memory: Brain, web_fetch: Globe, computer: Monitor, shell: Terminal, read_file: FileText, write_file: FileText,
  skill_view: GraduationCap, skill_propose: GraduationCap, save_skill: GraduationCap, rule_propose: GraduationCap, request_secret: KeyRound,
  search_chats: Search, read_chat: MessageSquare, attachment_to_computer: Monitor, read_task_attachment: FileText,
  create_document: FileText, update_document: FilePen, read_document: FileText, list_documents: FileText, web_search: Search,
  hand_over_task: UserRoundCheck, schedule: CalendarClock, suggest_project: FolderInput,
  browser_read: Globe, browser_act: MousePointerClick, browser_view: Smartphone, browser_screenshot: Camera,
  share_file: Download,
  list_connected_apps: Plug, add_mcp_server: Plug, update_connected_app: Plug, remove_connected_app: Plug,
  test_connected_app: Plug,
};
// The BusyBee.Day tools bots get from MCP (backend agents/busybee_tools.py) need no entry
// above: each has a bots.steps label, and its icon follows what it does.
const iconOf = (tool) => {
  if (ICONS[tool]) return ICONS[tool];
  if (/^(delete|bulk_delete)_/.test(tool)) return Trash2;
  if (/calendar|schedul|suggest_times/.test(tool)) return CalendarClock;
  if (/^(create|update|complete|duplicate|bulk|move|reorder|restore|mark)_/.test(tool)) return SquarePen;
  return ListChecks;
};
const isKnown = (t, tool) => !!ICONS[tool] || !!t(`bots.steps.${tool}`, { defaultValue: '' });
const TASK_TOOLS = new Set(['create_task', 'update_task', 'get_task', 'delete_task', 'hand_over_task']);
const TITLED = new Set([...TASK_TOOLS, 'skill_view', 'skill_propose', 'save_skill', 'read_file', 'write_file', 'create_document', 'update_document', 'read_document', 'web_search', 'schedule', 'browser_screenshot', 'share_file']);
// Files a step saved on the computer, to download from the step (backend routes/bots.py _step_view).
const FILE_TOOLS = new Set(['write_file', 'browser_screenshot']);

const siteOf = (url) => {
  try { return new URL(url).hostname.replace(/^www\./, ''); } catch { return url || ''; }
};

// What the bot said between its steps is plain text in the list: no headings or bold.
const plain = (text) => text.replace(/^#+\s*/gm, '').replace(/\*\*(.+?)\*\*/g, '$1').replace(/`([^`]+)`/g, '$1');

/** A step from the stored run (actions) or from the live stream (tool + args). */
const normalize = (step) => {
  if (!step.args) return step;
  const a = step.args || {};
  // Several desktop actions in one call: the page it opened, else its last action.
  const batch = Array.isArray(a.actions) ? a.actions.filter(Boolean) : [];
  const act = batch.find((x) => x.action === 'open_url') || batch[batch.length - 1] || a;
  const handed = Array.isArray(a.paths) ? a.paths.map((p) => String(p).split('/').pop()).join(', ') : undefined;
  return {
    tool: step.tool, ok: step.ok !== false, pending: !step.done,
    title: a.title || a.name || a.path || handed || a.query || (step.tool === 'shell' ? a.command : undefined),
    url: act.url, action: act.action || a.device, task_id: a.task_id,
  };
};

const stepLabel = (t, s) => {
  if (s.conn) return t('bots.steps.app', { app: s.conn, action: s.title || '' });
  if (!isKnown(t, s.tool)) return t('bots.steps.connected_app');
  if (s.tool === 'web_fetch') return t('bots.steps.web_fetch', { site: siteOf(s.url) });
  if (s.tool === 'computer') {
    return s.action === 'open_url' && s.url ? t('bots.steps.computer_open', { site: siteOf(s.url) }) : t('bots.steps.computer');
  }
  if (s.tool === 'browser_view') return t(s.action === 'phone' ? 'bots.steps.browser_view_phone' : 'bots.steps.browser_view_computer');
  if (TITLED.has(s.tool)) return s.title ? t(`bots.steps.${s.tool}`, { title: s.title }) : t(`bots.steps.${s.tool}_generic`);
  return t(`bots.steps.${s.tool}`);
};

const duration = (lang, from, to) => {
  const secs = Math.max(1, Math.round((new Date(to) - new Date(from)) / 1000));
  const [value, unit] = secs < 90 ? [secs, 'second'] : [Math.round(secs / 60), 'minute'];
  try {
    return new Intl.NumberFormat(lang, { style: 'unit', unit, unitDisplay: 'short' }).format(value);
  } catch {
    return `${value} ${unit === 'second' ? 's' : 'min'}`;
  }
};

const shotUrls = new Map();
const loadShot = (id) => {
  if (!shotUrls.has(id)) {
    shotUrls.set(id, botsApi.shotBlob(id).then((r) => URL.createObjectURL(r.data)).catch((e) => {
      shotUrls.delete(id);
      throw e;
    }));
  }
  return shotUrls.get(id);
};

/** The screenshots of a run's desktop steps, one after another (a replay). */
const ShotViewer = ({ shots, index, onIndex, onClose, t }) => {
  const [url, setUrl] = useState(null);
  const [failed, setFailed] = useState(false);
  const shot = shots[index];
  useEffect(() => {
    let alive = true;
    setUrl(null);
    setFailed(false);
    loadShot(shot.shot_id).then((u) => { if (alive) setUrl(u); }).catch(() => { if (alive) setFailed(true); });
    shots.slice(index + 1, index + 3).forEach((s) => loadShot(s.shot_id).catch(() => {}));  // the next ones, ahead
    return () => { alive = false; };
  }, [shot.shot_id, shots, index]);
  const go = (d) => onIndex(Math.min(shots.length - 1, Math.max(0, index + d)));
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-5xl w-[95vw] p-4 gap-3"
        onKeyDown={(e) => { if (e.key === 'ArrowLeft') go(-1); if (e.key === 'ArrowRight') go(1); }}>
        <DialogTitle className="text-sm font-medium pr-8">{stepLabel(t, shot)}</DialogTitle>
        <DialogDescription className="sr-only">{t('bots.steps.replay')}</DialogDescription>
        <div className="relative rounded-lg overflow-hidden bg-black aspect-[16/10] flex items-center justify-center" data-testid="shot-viewer">
          {url ? <img src={url} alt={stepLabel(t, shot)} className="max-w-full max-h-full object-contain" />
            : failed ? <span className="text-xs text-zinc-400 px-4 text-center">{t('bots.steps.shot_gone')}</span>
            : <Loader2 className="w-5 h-5 animate-spin text-zinc-500" />}
        </div>
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <button type="button" onClick={() => go(-1)} disabled={index === 0}
            className="inline-flex items-center gap-1 px-2 py-1 rounded hover:bg-accent disabled:opacity-30">
            <ChevronLeft className="w-4 h-4" />
          </button>
          <span>{index + 1} / {shots.length}</span>
          <button type="button" onClick={() => go(1)} disabled={index === shots.length - 1} data-testid="shot-next"
            className="inline-flex items-center gap-1 px-2 py-1 rounded hover:bg-accent disabled:opacity-30">
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// While the bot works only its latest steps show, like Grok Bot's ticker: the newest
// comes in at the bottom, the oldest leaves at the top; a click opens all of them.
const LIVE_ROWS = 3;

/**
 * What the bot did for an answer, like the step list under a Grok Bot or
 * Manus reply: collapsed to one line ("Done in 58 s", like OpenMausBot's "Worked
 * for"), opened to see every step. Task steps open the task, web steps open the page.
 * listOnly: just the steps, all of them (under the chat's live line, which is the header).
 * latest: with listOnly, only the latest steps sliding in (the live line's ticker).
 * said: what the bot wrote while it worked (backend conversation.split_answer), each before
 * the steps of the model call it came with, so the answer above is only the result.
 */
const RunSteps = ({ steps = [], said = [], running = false, ended = false, startedAt, finishedAt, onOpenTask, onDownload, listOnly = false, latest = false }) => {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(listOnly);
  const [viewing, setViewing] = useState(null);  // index into shots
  const rows = steps.map(normalize);
  const shots = rows.filter((s) => s.shot_id);
  if (rows.length === 0) return null;
  const expanded = open;
  const all = open && !latest;
  const first = all ? 0 : Math.max(0, rows.length - LIVE_ROWS);
  const shown = all ? rows : running ? rows.slice(first) : [];
  const time = !running && startedAt && finishedAt ? duration(i18n.language, startedAt, finishedAt) : null;
  const done = time && !ended;  // a failed or stopped run was not done: it keeps the step count
  const notes = all && said ? said : [];
  const before = {};  // step index -> what the bot said before it
  let k = 0;
  rows.forEach((s, i) => {
    while (k < notes.length && (notes[k].step == null || s.step == null || notes[k].step <= s.step)) {
      (before[i] = before[i] || []).push(notes[k]);
      k += 1;
    }
  });
  const sayRow = (n, key) => (
    <li key={key} className="whitespace-pre-line break-words text-foreground/75 py-0.5" data-testid="run-said">{plain(n.text)}</li>
  );
  const stepRow = (s, i) => {
    const Icon = s.conn || !isKnown(t, s.tool) ? Plug : iconOf(s.tool);
    const label = stepLabel(t, s);
    const failed = !s.ok && !s.pending;
    const content = (
      <>
        {s.pending ? <Loader2 className="w-3.5 h-3.5 shrink-0 animate-spin text-amber-500" />
          : failed ? <XCircle className="w-3.5 h-3.5 shrink-0 text-red-500" />
            : <Icon className="w-3.5 h-3.5 shrink-0" />}
        <span className={s.tool === 'shell' && s.title ? 'shrink-0' : 'truncate'}>{label}</span>
        {/* Which command: what the bot does on its computer is never a mystery. */}
        {s.tool === 'shell' && s.title && (
          <code className="truncate font-mono text-[11px] text-muted-foreground/70" data-testid="step-command">{s.title}</code>
        )}
        {failed && <span className="shrink-0 text-red-500">{t('bots.steps.failed')}</span>}
      </>
    );
    const cls = `flex items-center gap-2 min-w-0 max-w-full text-muted-foreground ${running && !all ? 'animate-in fade-in slide-in-from-bottom-1 duration-300' : ''}`;
    const key = `${i}-${s.tool}`;
    if (s.shot_id) {
      return (
        <li key={key}>
          <button type="button" onClick={() => setViewing(shots.indexOf(s))} className={`${cls} hover:text-foreground`}>
            {content}<Camera className="w-3 h-3 shrink-0 opacity-60" />
          </button>
        </li>
      );
    }
    if (TASK_TOOLS.has(s.tool) && s.task_id && s.tool !== 'delete_task' && onOpenTask) {
      return (
        <li key={key}>
          <button type="button" onClick={() => onOpenTask(s.task_id)} className={`${cls} hover:text-foreground`}>{content}</button>
        </li>
      );
    }
    if (FILE_TOOLS.has(s.tool) && s.title && s.ok && onDownload) {
      return (
        <li key={key}>
          <button type="button" onClick={() => onDownload(s.title)} className={`${cls} hover:text-foreground`}
            data-testid="step-download">{content}</button>
        </li>
      );
    }
    if (s.document_id && (s.tool === 'create_document' || s.tool === 'update_document')) {
      return (
        <li key={key}>
          <Link to={`/app/documents/${s.document_id}`} className={`${cls} hover:text-foreground`}>{content}</Link>
        </li>
      );
    }
    if (s.url && /^https?:\/\//.test(s.url)) {
      return (
        <li key={key}>
          <a href={s.url} target="_blank" rel="noopener noreferrer" className={`${cls} hover:text-foreground`}>{content}</a>
        </li>
      );
    }
    return <li key={key} className={cls} title={s.tool === 'shell' ? s.title : undefined}>{content}</li>;
  };
  return (
    <div className={listOnly ? 'mt-1 text-xs' : 'text-xs'} data-testid="run-steps">
      {!listOnly && (
      <button type="button" onClick={() => setOpen((o) => !o)} aria-expanded={expanded} data-testid="run-steps-toggle"
        className="inline-flex items-center gap-1.5 rounded-md py-0.5 text-muted-foreground hover:text-foreground">
        {running && !open ? <Loader2 className="w-3.5 h-3.5 animate-spin text-amber-500" />
          : done ? <Check className="w-3.5 h-3.5 text-emerald-500" />
            : <ChevronRight className={`w-3.5 h-3.5 transition-transform ${expanded ? 'rotate-90' : ''}`} />}
        <span>{done ? t('bots.steps.done_in', { time }) : t(running ? 'bots.steps.working' : 'bots.steps.summary', { count: rows.length })}</span>
        {done ? <ChevronRight className={`w-3 h-3 transition-transform ${expanded ? 'rotate-90' : ''}`} />
          : time && <span className="text-muted-foreground/60">· {time}</span>}
      </button>
      )}
      {shots.length > 0 && !running && !listOnly && (
        <button type="button" onClick={() => setViewing(0)} data-testid="steps-replay"
          className="ml-2 inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-muted-foreground hover:text-foreground hover:bg-accent">
          <Camera className="w-3.5 h-3.5" />{t('bots.steps.replay')}
        </button>
      )}
      {viewing !== null && shots[viewing] && (
        <ShotViewer shots={shots} index={viewing} onIndex={setViewing} onClose={() => setViewing(null)} t={t} />
      )}
      {shown.length > 0 && (
        <ol className="mt-1.5 ml-[7px] border-l border-border pl-3 space-y-1">
          {shown.map((s, j) => {
            const i = j + first;  // the step's place in the run: a stable key, so a new one slides in
            const row = stepRow(s, i);
            return before[i] ? [...before[i].map((n, m) => sayRow(n, `said-${i}-${m}`)), row] : row;
          })}
          {notes.slice(k).map((n, m) => sayRow(n, `said-end-${m}`))}
        </ol>
      )}
    </div>
  );
};

export default RunSteps;
